import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { getPortalMe, getPortalDistrictStudents, updatePortalDistrictTimes } from "../lib/api";
import PortalAttendance from "../components/portal/PortalAttendance";
import PortalAssessments from "../components/portal/PortalAssessments";
import PortalProgressReports from "../components/portal/PortalProgressReports";
import PortalContracts from "../components/portal/PortalContracts";
import PortalBilling from "../components/portal/PortalBilling";
import PortalTransport from "../components/portal/PortalTransport";
import PortalWeeklySheet from "../components/portal/PortalWeeklySheet";
import PortalProfile from "../components/portal/PortalProfile";
import ParentPortal from "../components/portal/ParentPortal";
import PortalCalendar from "../components/portal/PortalCalendar";
import { PortalChangePassword } from "../components/portal/PortalChangePassword";

export default function PortalPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(user);
  const [tab, setTab] = useState("attendance");

  useEffect(() => { if (user) setMe(user); }, [user]);
  useEffect(() => {
    getPortalMe().then((r) => setMe({ ...r.data, is_portal: true })).catch(() => {});
  }, []);

  if (!me) return null;

  const isLEA = me.role === "LEA";
  const isMedicaid = me.role === "Medicaid Billing";
  const isTransport = me.role === "Transportation";

  if (me.must_change_password) {
    return <PortalChangePassword forced onDone={async () => { const u = await refreshUser(); setMe(u); }} />;
  }

  // Parents/guardians use a dedicated, student-scoped read-only portal.
  if (me.role === "Parent") {
    return <ParentPortal me={me} />;
  }

  const tabs = isTransport
    ? [
        { key: "transport", label: "My Students" },
        { key: "weekly", label: "Weekly Overview" },
        { key: "calendar", label: "School Calendar" },
        { key: "profile", label: "My Profile" },
      ]
    : [
        { key: "attendance", label: "Attendance" },
        ...(isLEA ? [{ key: "assessments", label: "Assessments" }, { key: "progress", label: "Progress Reports" }, { key: "transport-times", label: "Pickup / Drop-off" }] : []),
        ...(isMedicaid ? [{ key: "billing", label: "DAT Log" }] : []),
        { key: "contracts", label: "Contracts" },
        { key: "calendar", label: "School Calendar" },
        { key: "profile", label: "My Profile" },
      ];

  const initialTab = isTransport ? "transport" : "attendance";
  const activeTab = tabs.some((t) => t.key === tab) ? tab : initialTab;
  const scopeLabel = isTransport ? me.provider : me.district;
  const portalTitle = isTransport ? "Transportation Portal" : "District Portal";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      {/* Header */}
      <header className="text-white" style={{ backgroundColor: "#00305E" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <img src="/logo_full.png" alt="SMARTIS" className="h-9 w-auto rounded bg-white/95 p-1" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight">{portalTitle}</h1>
            <p className="text-xs text-white/70 truncate" data-testid="portal-district-name">
              {scopeLabel} · {me.role}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium leading-tight">{me.name}</p>
              <p className="text-xs text-white/60">{me.email}</p>
            </div>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              className="text-sm bg-white/10 hover:bg-white/20 transition-colors rounded-md px-3 py-1.5"
              data-testid="portal-logout-btn"
            >
              Sign Out
            </button>
          </div>
        </div>
        {/* Tabs */}
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.key ? "border-white text-white" : "border-transparent text-white/60 hover:text-white/90"
              }`}
              data-testid={`portal-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === "attendance" && <PortalAttendance />}
        {activeTab === "assessments" && isLEA && <PortalAssessments />}
        {activeTab === "progress" && isLEA && <PortalProgressReports />}
        {activeTab === "billing" && isMedicaid && <PortalBilling />}
        {activeTab === "contracts" && (isLEA || isMedicaid) && <PortalContracts />}
        {activeTab === "transport" && isTransport && <PortalTransport />}
        {activeTab === "weekly" && isTransport && <PortalWeeklySheet />}
        {activeTab === "transport-times" && isLEA && (
          <PortalTransport
            fetchFn={getPortalDistrictStudents}
            updateFn={updatePortalDistrictTimes}
            intro="Update pickup and drop-off times for students in your district. Addresses are shown for your reference. Changing a pickup time notifies the student's guardians and the school; changing a drop-off time notifies the school."
            emptyText="No students found in your district yet."
          />
        )}
        {activeTab === "calendar" && <PortalCalendar />}
        {activeTab === "profile" && <PortalProfile me={me} onUpdated={setMe} />}
      </main>
    </div>
  );
}
